import { ArrowClockwiseIcon } from '@phosphor-icons/react'
import { AlertBanner, Button, Spinner } from '@curri/components'
import { useEffect, useState } from 'react'

import type { Resource } from '../../api/client.ts'
import {
  fetchAttemptsForDelivery,
  resendWebhookAttempt,
  WEBHOOK_READ_SCOPE,
  WEBHOOK_WRITE_SCOPE,
  type WebhookAttempt,
} from '../../api/webhooks.ts'
import { useAuth } from '../../auth/AuthProvider.tsx'
import { useApiFetch, useGhost } from '../../ghost/GhostProvider.tsx'
import { describeFailure as describeWebhookFailure } from '../webhooks/format.ts'
import { PANEL_HAIRLINE } from '../webhooks/Panel.tsx'

// The expanded row's "webhooks sent" card: every recorded attempt for this
// booking's delivery, fetched lazily — the component mounts only when the row
// opens, so a page of collapsed rows costs zero attempt reads.

/** The delivery status the body carried, lifted out by the service; an attempt
 *  stored without one renders as a generic label rather than a guess. */
const eventName = (attempt: WebhookAttempt): string =>
  attempt.deliveryStatus ?? 'webhook'

const attemptTime = (sentAt: string | null): string => {
  if (!sentAt) return '—'
  const date = new Date(sentAt)
  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString(undefined, { timeStyle: 'short' })}`
}

const HEADING = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--curri-text-tertiary)',
} as const

type ResendState =
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'queued' }
  | { status: 'failed'; message: string }

const ResendFailedControl = ({ attemptId }: { attemptId: number }) => {
  const apiFetch = useApiFetch()
  const { target: ghostTarget } = useGhost()
  const [state, setState] = useState<ResendState>({ status: 'idle' })

  const resend = async () => {
    setState({ status: 'sending' })
    const result = await resendWebhookAttempt(apiFetch, attemptId)
    setState(
      result.ok
        ? { status: 'queued' }
        : {
            status: 'failed',
            message: describeWebhookFailure(result.failure).body,
          }
    )
  }

  const isGhosting = ghostTarget !== null

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10,
        marginTop: 12,
      }}
    >
      <Button
        disabled={isGhosting || state.status === 'queued'}
        loading={state.status === 'sending'}
        onClick={() => void resend()}
        size="small"
        variant="secondary"
      >
        <ArrowClockwiseIcon />
        {state.status === 'queued' ? 'Queued' : 'Resend failed'}
      </Button>
      <span
        style={{
          fontSize: 12,
          color:
            state.status === 'failed'
              ? 'var(--color-text-danger)'
              : 'var(--curri-text-tertiary)',
        }}
      >
        {isGhosting
          ? 'Read-only while viewing as a partner.'
          : state.status === 'queued'
            ? 'Queued — the outcome lands as a new attempt.'
            : state.status === 'failed'
              ? state.message
              : 'Regenerated from the delivery’s current state.'}
      </span>
    </div>
  )
}

const AttemptLine = ({ attempt }: { attempt: WebhookAttempt }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 9,
      padding: '10px 0 0',
    }}
  >
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        flex: 'none',
        background: attempt.success
          ? 'var(--color-text-success)'
          : 'var(--color-text-danger)',
      }}
    />
    <span
      style={{
        flex: 1,
        minWidth: 0,
        fontFamily: 'var(--curri-font-mono)',
        fontSize: 12,
        color: 'var(--curri-text)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {eventName(attempt)}
    </span>
    <span
      style={{
        flex: 'none',
        fontFamily: 'var(--curri-font-mono)',
        fontSize: 11.5,
        color: attempt.success
          ? 'var(--curri-text-tertiary)'
          : 'var(--color-text-danger)',
      }}
    >
      {attempt.httpStatus === null
        ? attempt.success
          ? '—'
          : 'no response'
        : `${attempt.httpStatus}${attempt.success ? '' : ' failed'}`}
    </span>
    <span
      style={{
        flex: 'none',
        width: 96,
        textAlign: 'right',
        fontFamily: 'var(--curri-font-mono)',
        fontSize: 11,
        color: 'var(--curri-text-tertiary)',
      }}
    >
      {attemptTime(attempt.sentAt)}
    </span>
  </div>
)

/**
 * Rendered inside an expanded booking row. `deliveryExternalId` is null on a
 * payload row that has not booked a delivery yet — nothing has fired, and the
 * card says so instead of showing an empty list that reads as an outage.
 */
export const BookingHooks = ({
  deliveryExternalId,
}: {
  deliveryExternalId: string | null
}) => {
  const apiFetch = useApiFetch()
  const { state: authState } = useAuth()

  const grantedScopes =
    authState.status === 'authenticated' ? authState.caller.grantedScopes : []
  const canReadWebhooks = grantedScopes.includes(WEBHOOK_READ_SCOPE)
  const canResend = grantedScopes.includes(WEBHOOK_WRITE_SCOPE)

  const [resource, setResource] = useState<Resource<WebhookAttempt[]>>({
    status: 'loading',
  })

  useEffect(() => {
    if (deliveryExternalId === null || !canReadWebhooks) return

    const controller = new AbortController()
    setResource({ status: 'loading' })

    void (async () => {
      try {
        const result = await fetchAttemptsForDelivery(
          apiFetch,
          deliveryExternalId,
          controller.signal
        )
        if (controller.signal.aborted) return
        setResource(
          result.ok
            ? { status: 'ready', data: result.data.attempts }
            : { status: 'failed', failure: result.failure }
        )
      } catch {
        // Aborted.
      }
    })()

    return () => controller.abort()
  }, [apiFetch, deliveryExternalId, canReadWebhooks])

  const failedCount =
    resource.status === 'ready'
      ? resource.data.filter(attempt => !attempt.success).length
      : 0
  const newestFailed =
    resource.status === 'ready'
      ? resource.data.find(attempt => !attempt.success)
      : undefined

  return (
    <div
      style={{
        border: '1px solid var(--curri-border)',
        borderRadius: 12,
        padding: '14px 16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span style={HEADING}>Webhooks sent</span>
        <span style={{ flex: 1 }} />
        {resource.status === 'ready' && resource.data.length > 0 && (
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11,
              color: 'var(--curri-text-tertiary)',
            }}
          >
            {resource.data.length} recorded ·{' '}
            {failedCount === 0 ? 'all delivered' : `${failedCount} failed`}
          </span>
        )}
      </div>

      {deliveryExternalId === null ? (
        <p
          style={{
            margin: '10px 0 0',
            fontSize: 12.5,
            color: 'var(--curri-text-tertiary)',
          }}
        >
          No delivery yet — webhooks fire once the payload books.
        </p>
      ) : !canReadWebhooks ? (
        <p
          style={{
            margin: '10px 0 0',
            fontSize: 12.5,
            color: 'var(--curri-text-tertiary)',
          }}
        >
          This session cannot read webhook history — it was issued without the{' '}
          <code>{WEBHOOK_READ_SCOPE}</code> scope.
        </p>
      ) : resource.status === 'loading' ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 14 }}>
          <Spinner size="sm" />
        </div>
      ) : resource.status === 'failed' ? (
        <div style={{ marginTop: 10 }}>
          <AlertBanner type={describeWebhookFailure(resource.failure).tone}>
            {describeWebhookFailure(resource.failure).body}
          </AlertBanner>
        </div>
      ) : resource.data.length === 0 ? (
        <p
          style={{
            margin: '10px 0 0',
            fontSize: 12.5,
            color: 'var(--curri-text-tertiary)',
          }}
        >
          Nothing recorded for this delivery.
        </p>
      ) : (
        <div style={{ borderTop: `1px solid ${PANEL_HAIRLINE}`, marginTop: 10 }}>
          {resource.data.map(attempt => (
            <AttemptLine attempt={attempt} key={attempt.id} />
          ))}
          {/* One control, on the newest failure — redelivery regenerates from
              the delivery's current state, so resending older failures too
              would queue duplicate sends of the same present truth. */}
          {canResend && newestFailed !== undefined && (
            <ResendFailedControl attemptId={newestFailed.id} />
          )}
        </div>
      )}
    </div>
  )
}
